import { ArcElement, Chart as Chartjs, Title, Tooltip } from 'chart.js';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Skeleton from 'components/global/Skeleton';

import BaseChart, { CHART_TYPES } from './BaseChart';
import Legend from './CustomChartLegend';
import EmptyChart, { defaultEmptyChartFields } from './EmptyChart';
import * as S from './styled/DoughnutChart.styled';
import { useEmptyDataSettings } from './utils';

Chartjs.register(ArcElement, Tooltip, Title);

const options = {
	plugins: {
		legend: false,
		tooltip: {
			enabled: true
		}
	},
	responsive: true,
	layout: {
		padding: 2
	},
	datasets: {
		doughnut: {
			hoverOffset: 4,
			cutout: 55
		}
	}
};

const DoughnutChart = ({
	data,
	loading,
	innerTitle,
	width = '20rem',
	showLegend = true,
	emptyChartData,
	withCurrency,
	currency
}) => {
	const { t } = useTranslation();
	const {
		darkMode,
		localization: { dir }
	} = useSelector((state) => state.layout);

	const { emptyData } = useEmptyDataSettings();
	const noData = useMemo(() => !data || data.length === 0 || data?.labels?.length === 0, [data]);

	const totalValue = useMemo(() => {
		let calc = 0;
		if (data?.datasets?.[0]?.data) {
			for (const value of data.datasets[0].data) {
				calc += Number(value);
			}
		}
		return withCurrency ? `${calc} ${currency}` : calc;
	}, [data]);

	if (loading) {
		return (
			<S.Container>
				<Skeleton width={width} height={width} borderRadius="100%" />
			</S.Container>
		);
	}

	return (
		<S.Container>
			{!data || data?.labels?.length === 0 ? (
				<EmptyChart {...(emptyChartData ? emptyChartData : defaultEmptyChartFields)} />
			) : (
				<>
					<S.ChartContainer width={width}>
						<BaseChart
							type={CHART_TYPES.doughnut}
							data={noData ? emptyData : data}
							options={{ ...options }}
							plugins={[
								{
									afterDatasetsDraw(chart) {
										// Get the chart area
										const width = chart.width,
											height = chart.height,
											ctx = chart.ctx;

										// clean the canvas
										ctx.restore();

										// font config
										ctx.font = `bold 2.5rem ${dir === 'rtl' ? '"Ping", "PingLCG"' : '"PingLCG", "Ping"'}, sans-serif`;
										ctx.fillStyle = darkMode ? '#fff' : '#000';
										ctx.textBaseline = 'baseline';

										// font config
										let text = totalValue.toString();
										const maxTextWidth = width - 75; // Adjust as needed
										if (ctx.measureText(text).width > maxTextWidth) {
											while (ctx.measureText(`${text}...`).width > maxTextWidth) {
												text = text.slice(0, -1);
											}
											text += '...';
										}
										let textX = Math.round(
											(dir === 'rtl'
												? width + ctx.measureText(text).width
												: width - ctx.measureText(text).width) / 2
										);
										let textY = height / 2 - 5;
										ctx.fillText(text, textX, textY);

										ctx.font = `${'normal'} 1.5rem ${dir === 'rtl' ? '"Ping", "PingLCG"' : '"PingLCG", "Ping"'}, sans-serif`;
										ctx.fillStyle = darkMode ? '#fff' : '#000';
										ctx.textBaseline = 'baseline';

										(text = t(innerTitle)),
											(textX = Math.round(
												(dir === 'rtl'
													? width + ctx.measureText(text).width
													: width - ctx.measureText(text).width) / 2
											)),
											(textY = height / 2);
										ctx.fillText(text, textX, textY + 20);

										if (data?.time) {
											ctx.font = `${'normal'} 1.5rem ${dir === 'rtl' ? '"Ping", "PingLCG"' : '"PingLCG", "Ping"'}, sans-serif`;
											ctx.fillStyle = darkMode ? '#fff' : '#000';
											ctx.textBaseline = 'baseline';
											(text = data?.time),
												(textX = Math.round(
													(dir === 'rtl'
														? width + ctx.measureText(text).width
														: width - ctx.measureText(text).width) / 2
												)),
												(textY = height / 2);
											ctx.fillText(text, textX, textY + 20);
										}
										ctx.save();
									}
								}
							]}
						/>
					</S.ChartContainer>
					{showLegend && <Legend data={data} withCurrency={withCurrency} currency={currency} />}
				</>
			)}
		</S.Container>
	);
};

export default DoughnutChart;
